import * as React from 'react'
import { Children, FC, useContext, useEffect, useState } from 'react'
import { LayoutChangeEvent, LogBox } from 'react-native'
import { ThemeContext } from 'styled-components/native'
import { BorderProps, LayoutProps, SpaceProps } from 'styled-system'
import { ColorOptions, RadiiOptions, SpaceOptions } from '../../theme/UiThemeProvider'
import { useBreakPointProps } from '../../utils/utils'
import { SpacerNew } from '../SpacerNew'
import { boxTypes } from './BoxTypes'
import { customBorderTypes } from './CustomBorderTypes'
import { customShadowTypes } from './CustomShadowTypes'
import { shadowTypes } from './ShadowTypes'
import { StyledSafeAreaView } from './StyledSafeAreaView'
import { StyledTouchable } from './StyledTouchable'
import { StyledView } from './StyledView'

export { customBorderTypes, customShadowTypes }

LogBox.ignoreLogs(['Expected style "shadowOpacity', 'Node of type'])

interface Aliases {
  justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'
  align?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline'
  direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse'
  f?: number
  ph?: SpaceOptions | number | string
  pv?: SpaceOptions | number | string
  w?: string | number
  h?: string | number
}

const AliasMap = {
  justify: 'justifyContent',
  align: 'alignItems',
  direction: 'flexDirection',
  f: 'flex',
  pv: 'paddingVertical',
  ph: 'paddingHorizontal',
  w: 'width',
  h: 'height',
}

type FlexAlignType = 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline'

export interface BoxProps extends SpaceProps, BorderProps, LayoutProps, Aliases {
  type?: keyof typeof boxTypes | Array<keyof typeof boxTypes>
  alignContent?:
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'stretch'
    | 'space-between'
    | 'space-around'
    | Array<'flex-start' | 'flex-end' | 'center' | 'stretch' | 'space-between' | 'space-around'>
  alignItems?: FlexAlignType | Array<FlexAlignType>
  alignSelf?: 'auto' | FlexAlignType | Array<'auto' | FlexAlignType>
  aspectRatio?: number | Array<number>
  bottom?: number | string | Array<number | string>
  display?: 'none' | 'flex' | Array<'none' | 'flex'>
  end?: number | string | Array<number | string>
  flex?: number | Array<number>
  flexBasis?: number | string | Array<number | string>
  flexDirection?:
    | 'row'
    | 'column'
    | 'row-reverse'
    | 'column-reverse'
    | Array<'row' | 'column' | 'row-reverse' | 'column-reverse'>
  flexGrow?: number | Array<number>
  flexShrink?: number | Array<number>
  flexWrap?: 'wrap' | 'nowrap' | 'wrap-reverse' | Array<'wrap' | 'nowrap' | 'wrap-reverse'>
  bg?: ColorOptions | Array<ColorOptions>
  backgroundColor?: ColorOptions | Array<ColorOptions>
  justifyContent?:
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
    | Array<'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'>
  left?: number | string | Array<number | string>
  margin?: number | string | Array<number | string>
  marginBottom?: number | string | Array<number | string>
  marginEnd?: number | string | Array<number | string>
  marginHorizontal?: number | string | Array<number | string>
  marginLeft?: number | string | Array<number | string>
  marginRight?: number | string | Array<number | string>
  marginStart?: number | string | Array<number | string>
  marginTop?: number | string | Array<number | string>
  marginVertical?: number | string | Array<number | string>
  maxHeight?: number | string | Array<number | string>
  maxWidth?: number | string | Array<number | string>
  minHeight?: number | string | Array<number | string>
  minWidth?: number | string | Array<number | string>
  overflow?: 'visible' | 'hidden' | 'scroll' | 'auto' | Array<'visible' | 'hidden' | 'scroll' | 'auto'>
  position?: 'absolute' | 'relative' | 'sticky' | Array<'absolute' | 'relative' | 'sticky'>
  right?: number | string | Array<number | string>
  start?: number | string | Array<number | string>
  top?: number | string | Array<number | string>
  zIndex?: number | Array<number>
  ID?: string
  opacity?: number | Array<number>
  underlayColor?: ColorOptions | Array<ColorOptions>
  padding?: SpaceOptions | number | Array<SpaceOptions | number>
  paddingLeft?: SpaceOptions | number | Array<SpaceOptions | number>
  paddingRight?: SpaceOptions | number | Array<SpaceOptions | number>
  paddingTop?: SpaceOptions | number | Array<SpaceOptions | number>
  paddingBottom?: SpaceOptions | number | Array<SpaceOptions | number>
  paddingHorizontal?: SpaceOptions | number | Array<SpaceOptions | number>
  paddingVertical?: SpaceOptions | number | Array<SpaceOptions | number>
  borderEndWidth?: SpaceOptions | number | Array<SpaceOptions | number>
  borderStartWidth?: SpaceOptions | number | Array<SpaceOptions | number>
  borderWidth?: SpaceOptions | number | Array<SpaceOptions | number>
  borderTopWidth?: SpaceOptions | number | Array<SpaceOptions | number>
  borderBottomWidth?: SpaceOptions | number | Array<SpaceOptions | number>
  borderLeftWidth?: SpaceOptions | number | Array<SpaceOptions | number>
  borderRightWidth?: SpaceOptions | number | Array<SpaceOptions | number>
  borderRadius?: RadiiOptions | string | number | Array<RadiiOptions | string | number>
  borderTopLeftRadius?: RadiiOptions | number | string | Array<RadiiOptions | number>
  borderTopRightRadius?: RadiiOptions | number | string | Array<RadiiOptions | number>
  borderBottomLeftRadius?: RadiiOptions | number | string | Array<RadiiOptions | number>
  borderBottomRightRadius?: RadiiOptions | number | string | Array<RadiiOptions | number>
  borderColor?: ColorOptions | Array<ColorOptions>
  borderTopColor?: ColorOptions | Array<ColorOptions>
  borderBottomColor?: ColorOptions | Array<ColorOptions>
  borderLeftColor?: ColorOptions | Array<ColorOptions>
  borderRightColor?: ColorOptions | Array<ColorOptions>
  borderStyle?: 'solid' | 'dotted' | 'dashed' | Array<'solid' | 'dotted' | 'dashed'>
  shadowType?: keyof typeof shadowTypes | Array<keyof typeof shadowTypes>
  shadowColor?: ColorOptions | Array<ColorOptions>
  shadowOpacity?: number | Array<number>
  shadowRadius?: number | Array<number>
  hoverColor?: ColorOptions | Array<ColorOptions>
  onHover?: (isHovering: boolean) => void
  safeArea?: boolean | Array<boolean>
  disabled?: boolean | Array<boolean>
  onPress?: (evt?: any) => any
  width?: number | string | Array<number | string>
  height?: number | string | Array<number | string>
  p?: SpaceOptions | number | Array<SpaceOptions | number | string>
  pl?: SpaceOptions | number | Array<SpaceOptions | number | string>
  pr?: SpaceOptions | number | Array<SpaceOptions | number | string>
  pt?: SpaceOptions | number | Array<SpaceOptions | number | string>
  pb?: SpaceOptions | number | Array<SpaceOptions | number | string>
  gap?: SpaceOptions | number | Array<SpaceOptions | number>
  customBorderStyle?: keyof typeof customBorderTypes
  customShadowStyle?: keyof typeof customShadowTypes
  onPressIn?: () => void
  onPressOut?: () => void
  pointerEvents?: 'box-none' | 'none' | 'box-only' | 'auto' | Array<'box-none' | 'none' | 'box-only' | 'auto'>
  nativeID?: string
  testID?: string
  onLayout?: (event: LayoutChangeEvent) => void
  accessibilityLabel?: string
  backgroundImage?: string
  children?: any
}

export const Box: FC<BoxProps> = React.forwardRef((props, ref) => {
  const { children } = props

  let modifiedProps = useBreakPointProps<BoxProps>(props)
  Object.keys(AliasMap).forEach((item) => {
    if (modifiedProps[item as keyof typeof modifiedProps] !== undefined) {
      modifiedProps = { ...modifiedProps, [AliasMap[item]]: modifiedProps[item as keyof typeof modifiedProps] }
    }
  })

  const {
    shadowType,
    type,
    safeArea,
    disabled = false,
    backgroundColor,
    bg,
    onHover,
    hoverColor,
    gap,
    flexDirection,
    underlayColor: propUnderlayColor,
  } = modifiedProps
  const onPress = modifiedProps.onPress
  const [currentBg, setCurrentBg] = useState(backgroundColor ?? bg)
  const [isHovering, setIsHovering] = useState(false)
  const theme = useContext(ThemeContext)

  useEffect(() => {
    if (!isHovering || !hoverColor) {
      setCurrentBg(backgroundColor ?? bg)
    } else if (isHovering && hoverColor) {
      setCurrentBg(hoverColor)
    }
  }, [isHovering, hoverColor, backgroundColor, bg])

  const onMouseEnter = () => {
    // TODO: Need discussion.
    // To stop background color change on hover
    if (disabled) {
      return
    }
    if (hoverColor) {
      setIsHovering(true)
    }
    if (onHover) {
      onHover(true)
    }
  }

  const onMouseLeave = () => {
    // To stop background color change on hover
    if (disabled) {
      return
    }
    if (hoverColor) {
      setIsHovering(false)
    }
    if (onHover) {
      onHover(false)
    }
  }

  let underlayColor = propUnderlayColor

  if (propUnderlayColor && theme?.colors[propUnderlayColor as ColorOptions]) {
    underlayColor = theme?.colors[propUnderlayColor as ColorOptions]
  }

  let contents = children
  let firstIndex: number | undefined

  if (gap) {
    let spacerDirection: 'vertical' | 'horizontal' = 'vertical'
    if (flexDirection && flexDirection.toString().startsWith('row')) {
      spacerDirection = 'horizontal'
    }
    const tempContent = [] as Array<React.ReactNode>
    Children.forEach(children, (child, index) => {
      if (child) {
        if (firstIndex === undefined) {
          firstIndex = index
        } else {
          let currentGap: SpaceOptions | number = gap as SpaceOptions | number
          if (Array.isArray(gap)) {
            currentGap = gap[index - 1]
          }
          tempContent.push(<SpacerNew key={`gap-${index}`} direction={spacerDirection} size={currentGap} />)
        }
      }
      tempContent.push(child)
    })
    contents = tempContent
  }

  const Container: FC<any> = safeArea ? StyledSafeAreaView : onPress ? StyledTouchable : StyledView

  return (
    <Container
      {...boxTypes[type as keyof typeof boxTypes]}
      {...shadowTypes[shadowType as keyof typeof shadowTypes]}
      {...modifiedProps}
      disabled={disabled}
      underlayColor={underlayColor}
      bg={currentBg}
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      {contents}
    </Container>
  )
})
